import { useEffect, useId, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import CoinIcon from '../components/CoinIcon'
import Spinner from '../components/Spinner'
import useLockACall from '../hooks/useLockACall'
import { callPath } from '../routes'
import { callsAPI, marketAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { CallExpiry, CallOptions, TickerQuote } from '../types'
import { apiErrorMessage } from '../utils/apiError'
import { displaySymbol } from '../utils/displaySymbol'
import { formatPrice } from '../utils/marketPrice'

const EXPIRY_LABELS: Record<CallExpiry, string> = {
  '24h': '24 hours',
  '3d': '3 days',
  '7d': '7 days',
  '14d': '14 days',
}

export const PERMANENCE_TEXT =
  "I understand this call is public and permanent: it can't be edited or deleted, and it counts whether it wins or loses."

/** Signed percent from ``from`` to ``to``, e.g. "-4.20%". */
const pct = (from: number, to: number) => {
  const value = ((to - from) / from) * 100
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
}

/** A typed stop or target, shown like the entry (formatPrice); a dash until it is a price. */
export const previewLevel = (raw: string): string => {
  const value = Number(raw)
  return raw.trim() !== '' && Number.isFinite(value) && value > 0 ? formatPrice(value) : '—'
}

type Direction = 'long' | 'short'

/**
 * What is wrong with the levels against the live price, or null if nothing.
 * The server checks the same rules against the price it locks at, which may
 * have moved a little by then.
 */
export const levelProblem = (
  direction: Direction,
  price: number,
  stop: number,
  target: number,
  options: Pick<CallOptions, 'min_stop_distance_pct' | 'max_stop_distance_pct'>
): string | null => {
  const long = direction === 'long'
  if (long ? !(stop < price) : !(stop > price)) return `A ${direction}'s stop must be ${long ? 'below' : 'above'} the price.`
  if (long ? !(target > price) : !(target < price))
    return `A ${direction}'s target must be ${long ? 'above' : 'below'} the price.`
  const distance = (Math.abs(price - stop) / price) * 100
  if (distance < options.min_stop_distance_pct)
    return `The stop is ${distance.toFixed(2)}% away. It must be at least ${options.min_stop_distance_pct}% from the price.`
  if (distance > options.max_stop_distance_pct)
    return `The stop is ${distance.toFixed(2)}% away. It can be at most ${options.max_stop_distance_pct}% from the price.`
  return null
}

const LockCallForm = ({ options }: { options: CallOptions }) => {
  const navigate = useNavigate()
  const handle = useAuthStore((st) => st.user?.username ?? '')
  const ids = useId()
  const [symbol, setSymbol] = useState(options.symbols[0] ?? '')
  const [direction, setDirection] = useState<Direction>('long')
  const [stop, setStop] = useState('')
  const [target, setTarget] = useState('')
  const [expiry, setExpiry] = useState<CallExpiry>('3d')
  const [note, setNote] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [tickers, setTickers] = useState<TickerQuote[] | null>(null)

  // The same 60-second ticker the dashboard tape reads: a preview only. The
  // call is locked at the price the server fetches when it is submitted.
  useEffect(() => {
    let live = true
    const load = () =>
      marketAPI
        .getTicker()
        .then((tape) => live && setTickers(tape.tickers))
        .catch(() => live && setTickers((prev) => prev ?? []))
    load()
    const timer = window.setInterval(load, 60_000)
    return () => {
      live = false
      window.clearInterval(timer)
    }
  }, [])

  const price = tickers?.find((t) => t.symbol === symbol)?.price ?? null
  const stopValue = Number(stop)
  const targetValue = Number(target)
  const levelsEntered = stop !== '' && target !== '' && stopValue > 0 && targetValue > 0
  const problem =
    price !== null && levelsEntered ? levelProblem(direction, price, stopValue, targetValue, options) : null
  const rr =
    price !== null && levelsEntered && Math.abs(price - stopValue) > 0
      ? Math.abs(targetValue - price) / Math.abs(price - stopValue)
      : null
  const noteTooLong = note.length > options.note_max_chars
  const canSubmit = levelsEntered && acknowledged && !problem && !noteTooLong && !submitting

  const submit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      const locked = await callsAPI.lock({
        symbol,
        direction,
        stop: stopValue,
        target: targetValue,
        expiry,
        note: note.trim() || null,
        acknowledged_permanent: true,
      })
      toast.success(`Call #${locked.call.id} locked at ${formatPrice(locked.entry_price)}`)
      navigate(callPath(locked.call.id), { state: { justLocked: true } })
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not lock the call'))
      setSubmitting(false)
    }
  }

  const input =
    'w-full rounded-md border border-terminal-border bg-terminal-bg px-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent'

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <form
        className="space-y-5 rounded-xl border border-terminal-border bg-terminal-surface p-4 md:p-6"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        data-testid="lock-call-form"
      >
        <div>
          <label htmlFor={`${ids}-coin`} className="mb-1 block text-sm font-medium text-gray-300">
            Coin
          </label>
          <div className="flex items-center gap-3">
            <CoinIcon symbol={symbol} size={28} />
            <select id={`${ids}-coin`} value={symbol} onChange={(e) => setSymbol(e.target.value)} className={input}>
              {options.symbols.map((s) => (
                <option key={s} value={s}>
                  {displaySymbol(s)}
                </option>
              ))}
            </select>
          </div>
          <p className="mt-1 text-sm text-gray-400" data-testid="live-price">
            {price !== null ? (
              <>
                Live price <span className="font-mono text-gray-100">{formatPrice(price)}</span>
              </>
            ) : tickers === null ? (
              'Loading the live price…'
            ) : (
              'No live price right now. The call locks at the price Kraken gives when you submit.'
            )}
          </p>
        </div>

        <fieldset>
          <legend className="mb-1 block text-sm font-medium text-gray-300">Direction</legend>
          <div className="flex gap-2" role="group" aria-label="Direction">
            {(['long', 'short'] as const).map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={direction === d}
                onClick={() => setDirection(d)}
                className={`flex-1 rounded-md border px-4 py-2 text-sm font-semibold capitalize ${
                  direction === d
                    ? d === 'long'
                      ? 'border-green-500 bg-green-500/15 text-green-200'
                      : 'border-red-500 bg-red-500/15 text-red-200'
                    : 'border-terminal-border text-gray-400 hover:text-gray-200'
                }`}
              >
                {d === 'long' ? 'Long' : 'Short'}
              </button>
            ))}
          </div>
        </fieldset>

        <div>
          <span className="mb-1 block text-sm font-medium text-gray-300">Entry</span>
          <p className="rounded-md border border-dashed border-terminal-border px-3 py-2 text-sm text-gray-300" data-testid="entry-market">
            Market — the live price when you lock
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor={`${ids}-stop`} className="mb-1 block text-sm font-medium text-gray-300">
              Stop
            </label>
            <input
              id={`${ids}-stop`}
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              value={stop}
              onChange={(e) => setStop(e.target.value)}
              className={input}
            />
            {price !== null && stop !== '' && stopValue > 0 && (
              <p className="mt-1 text-xs text-gray-400" data-testid="stop-hint">
                {pct(price, stopValue)} from the live price
              </p>
            )}
          </div>
          <div>
            <label htmlFor={`${ids}-target`} className="mb-1 block text-sm font-medium text-gray-300">
              Target
            </label>
            <input
              id={`${ids}-target`}
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className={input}
            />
            {price !== null && target !== '' && targetValue > 0 && (
              <p className="mt-1 text-xs text-gray-400" data-testid="target-hint">
                {pct(price, targetValue)} from the live price
              </p>
            )}
          </div>
        </div>
        {rr !== null && !problem && (
          <p className="text-sm text-gray-300" data-testid="rr-hint">
            Reward to risk about <span className="font-mono text-gray-100">{rr.toFixed(2)}R</span> at the live price. A win
            scores what the move made in R; a stop scores about −1R.
          </p>
        )}
        {problem && (
          <p className="text-sm text-amber-300" role="alert" data-testid="level-problem">
            {problem}
          </p>
        )}

        <fieldset>
          <legend className="mb-1 block text-sm font-medium text-gray-300">Expires in</legend>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Expires in">
            {options.expiries.map((e) => (
              <button
                key={e}
                type="button"
                aria-pressed={expiry === e}
                onClick={() => setExpiry(e)}
                className={`rounded-full border px-4 py-1.5 text-sm ${
                  expiry === e ? 'border-accent bg-accent/15 text-accent' : 'border-terminal-border text-gray-400 hover:text-gray-200'
                }`}
              >
                {EXPIRY_LABELS[e]}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500">If neither level is hit by then, it closes at the market price.</p>
        </fieldset>

        <div>
          <label htmlFor={`${ids}-note`} className="mb-1 block text-sm font-medium text-gray-300">
            Note <span className="font-normal text-gray-500">(optional, public, can&apos;t be edited)</span>
          </label>
          <textarea
            id={`${ids}-note`}
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={input}
          />
          <p className={`mt-1 text-xs ${noteTooLong ? 'text-amber-300' : 'text-gray-500'}`} data-testid="note-count">
            {note.length} / {options.note_max_chars}
          </p>
        </div>

        <label className="flex items-start gap-3 text-sm text-gray-300">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 h-4 w-4"
            data-testid="permanence-checkbox"
          />
          <span>{PERMANENCE_TEXT}</span>
        </label>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-terminal-bg hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? 'Locking…' : 'Lock call at live price'}
        </button>
      </form>

      <aside className="space-y-4" data-testid="receipt-preview">
        <div className="rounded-xl border border-terminal-border bg-terminal-surface p-4">
          <p className="text-xs uppercase tracking-wider text-gray-500">Receipt preview</p>
          <p className="mt-2 text-lg font-bold text-gray-50">
            {displaySymbol(symbol)} <span className={direction === 'long' ? 'text-green-300' : 'text-red-300'}>{direction === 'long' ? 'Long' : 'Short'}</span>
          </p>
          <dl className="mt-2 space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-400">Entry</dt>
              <dd className="font-mono text-gray-200" data-testid="preview-entry">
                {price !== null ? `≈ ${formatPrice(price)}` : 'at lock'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-400">Stop</dt>
              <dd className="font-mono text-gray-200" data-testid="preview-stop">
                {previewLevel(stop)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-400">Target</dt>
              <dd className="font-mono text-gray-200" data-testid="preview-target">
                {previewLevel(target)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-400">Expires in</dt>
              <dd className="text-gray-200">{EXPIRY_LABELS[expiry]}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-400">By</dt>
              <dd className="text-gray-200">@{handle}</dd>
            </div>
          </dl>
          {note.trim() && <p className="mt-3 border-t border-terminal-border pt-3 text-sm italic text-gray-300">“{note.trim()}”</p>}
        </div>
        <ol className="space-y-3 rounded-xl border border-terminal-border bg-terminal-surface p-4 text-sm text-gray-300" data-testid="lock-steps">
          <li>
            <span className="font-semibold text-gray-100">1. Locked.</span> Entry is the live Kraken price at that moment.
            The call is fingerprinted at once, with your handle and note.
          </li>
          <li>
            <span className="font-semibold text-gray-100">2. Tracked every 5 min.</span> On live Kraken prices, until the
            stop, the target or the expiry.
          </li>
          <li>
            <span className="font-semibold text-gray-100">3. Scored in R.</span> By the same fixed rules as every signal on
            FinCreator, win or lose, on its public receipt.
          </li>
        </ol>
      </aside>
    </div>
  )
}

/** /calls/new: lock a call. Hidden unless the server says the feature is on for this user. */
const LockCall = () => {
  const options = useLockACall()
  if (options === undefined)
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  if (options === null) return <Navigate to="/" replace />
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-50">Lock a call</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Your call, locked at the live price and scored by fixed public rules. Up to {options.max_open_calls} open at once.
        </p>
      </div>
      <LockCallForm options={options} />
    </div>
  )
}

export default LockCall
